import * as csc from 'cornerstone-core';
import * as _ from 'lodash';
import {
  BaseAnnotationTool,
  draw,
  drawCircle,
  drawHandles,
  drawLine,
  getNewContext,
  getToolForElement,
  getToolState,
  toolColors,
} from 'app/CornerstoneTools';
import compute3PointCircle from 'app/CornerstoneTools/geometry/compute3PointCircle';
import computeDistractionIndex from 'app/CornerstoneTools/DistractionIndexTool/computeDistractionIndex';
import checkPointNearHandles from 'app/CornerstoneTools/checkPointNearHandles';
import ChainingToolBuilder from 'app/CornerstoneTools/ChainingToolBuilder';
import {
  EventWithCornerstoneDetail,
  DistractionIndexAnnotation,
  Point,
  RenderEventWithCornerstoneDetail,
  DistractionIndexPoints,
} from 'app/CornerstoneTools/types';
import { IntlShape } from 'react-intl';
import { BaseToolConfiguration, EVENTS, textStyle } from 'cornerstone-tools';
import { drawToolStepExplanations } from 'app/CornerstoneTools/utils/toolStepsExplanations';
import {
  projectPerpendicularToAxis,
  scaleDistanceToCanvas,
} from 'app/CornerstoneTools/pixelToCanvasUtils';
import { getMiddlePoint } from 'app/CornerstoneTools/geometryHelper';

interface DistractionIndexToolConfiguration extends BaseToolConfiguration {
  dicomDataPrinterToolName: string;
  drawHandles: boolean;
  drawHandlesOnHover: boolean;
  hideHandlesIfMoving: boolean;
  renderDashed: boolean;
  getIntl: () => IntlShape;
}

const drawnCircleWithCenter = (
  ctx: CanvasRenderingContext2D,
  element: HTMLElement,
  center: Point | undefined,
  mainRadius: number | undefined,
  mainCircleOptions: any,
  centerRadius: number,
  centerOptions: any
): void => {
  if (center && mainRadius) {
    drawCircle(ctx, element, center, mainRadius, mainCircleOptions);
    drawCircle(ctx, element, center, centerRadius, centerOptions);
  }
};

const computeDistractionIndexPoints = (
  CTF1: Point,
  CTF2: Point,
  CTF3: Point,
  CCA1: Point,
  CCA2: Point,
  CCA3: Point
): DistractionIndexPoints => {
  const { center: femoralHeadCenter, radius: femoralHeadRadius } =
    compute3PointCircle(CTF1, CTF2, CTF3) ?? {};
  const { center: acetabularCavityCenter, radius: acetabularCavityRadius } =
    compute3PointCircle(CCA1, CCA2, CCA3) ?? {};

  const distractionIndex = computeDistractionIndex(
    femoralHeadCenter,
    femoralHeadRadius,
    acetabularCavityCenter
  );
  return {
    femoralHeadCenter,
    femoralHeadRadius,
    acetabularCavityCenter,
    acetabularCavityRadius,
    distractionIndex,
  };
};

export default class DistractionIndexTool extends BaseAnnotationTool {
  public toolBuilder: ChainingToolBuilder<DistractionIndexAnnotation>;
  public addNewMeasurement: ChainingToolBuilder<DistractionIndexAnnotation>['addNewMeasurement'];
  public pointNearTool: typeof checkPointNearHandles;

  constructor(props: Partial<{ configuration: Partial<DistractionIndexToolConfiguration> }> = {}) {
    const defaultProps = {
      name: 'DistractionIndexTool',
      supportedInteractionTypes: ['Mouse', 'Touch'] as const,
      configuration: {
        dicomDataPrinterToolName: 'DicomDataPrinter',
        drawHandles: true,
        drawHandlesOnHover: false,
        hideHandlesIfMoving: false,
        renderDashed: false,
        getIntl: () => ({} as IntlShape),
      } as DistractionIndexToolConfiguration,
    };

    super(props, defaultProps);
    this.toolBuilder = new ChainingToolBuilder({
      name: this.name,
      createNewMeasurement: this.createNewMeasurement,
      handlesKeys: [
        'CTF11',
        'CTF12',
        'CTF13',
        'CCA11',
        'CCA12',
        'CCA13',
        'CTF21',
        'CTF22',
        'CTF23',
        'CCA21',
        'CCA22',
        'CCA23',
      ],
      maximumToolData: 1,
    });
    this.addNewMeasurement = this.toolBuilder.addNewMeasurement;
    this.pointNearTool = checkPointNearHandles;
  }

  get configuration(): DistractionIndexToolConfiguration {
    return super.configuration as DistractionIndexToolConfiguration;
  }

  createNewMeasurement = (
    eventData: EventWithCornerstoneDetail['detail']
  ): DistractionIndexAnnotation => ({
    visible: true,
    active: true,
    color: undefined,
    invalidated: true,
    handles: {
      CTF11: {
        x: eventData.currentPoints.image.x,
        y: eventData.currentPoints.image.y,
        highlight: true,
        active: true,
      },
    } as DistractionIndexAnnotation['handles'],
  });

  updateCachedStats = (
    _image: csc.Image,
    element: HTMLElement & EventTarget,
    data: DistractionIndexAnnotation
  ): void => {
    const { CTF11, CTF12, CTF13, CCA11, CCA12, CCA13, CTF21, CTF22, CTF23, CCA21, CCA22, CCA23 } =
      data.handles;
    const distractionIndexPoints1 = computeDistractionIndexPoints(
      CTF11,
      CTF12,
      CTF13,
      CCA11,
      CCA12,
      CCA13
    );
    const distractionIndexPoints2 = computeDistractionIndexPoints(
      CTF21,
      CTF22,
      CTF23,
      CCA21,
      CCA22,
      CCA23
    );
    let leftIndex;
    let rightIndex;
    if (distractionIndexPoints1.femoralHeadCenter && distractionIndexPoints2.femoralHeadCenter) {
      const femoralHeadCenter1InCanvas = csc.pixelToCanvas(
        element,
        distractionIndexPoints1.femoralHeadCenter as csc.PixelCoordinate
      );
      const femoralHeadCenter2InCanvas = csc.pixelToCanvas(
        element,
        distractionIndexPoints2.femoralHeadCenter as csc.PixelCoordinate
      );
      const isPoints1OnTheLeft = femoralHeadCenter1InCanvas.x <= femoralHeadCenter2InCanvas.x;
      if (isPoints1OnTheLeft) {
        leftIndex = distractionIndexPoints1.distractionIndex;
        rightIndex = distractionIndexPoints2.distractionIndex;
      } else {
        leftIndex = distractionIndexPoints2.distractionIndex;
        rightIndex = distractionIndexPoints1.distractionIndex;
      }
    }
    _.merge(data, { distractionIndexPoints1, distractionIndexPoints2, leftIndex, rightIndex });

    const eventType = EVENTS.MEASUREMENT_MODIFIED;
    const modifiedEventData = {
      toolName: this.name,
      toolType: this.name, // Deprecation notice: toolType will be replaced by toolName
      element,
      measurementData: data,
    };

    // @ts-ignore Need to be ignored due to conflict in types
    csc.triggerEvent(element, eventType, modifiedEventData);
  };

  drawDistractionIndex = (
    element: HTMLElement,
    ctx: CanvasRenderingContext2D,
    {
      femoralHeadCenter,
      femoralHeadRadius,
      acetabularCavityCenter,
      acetabularCavityRadius,
      distractionIndex,
    }: DistractionIndexPoints,
    { color }: { color: string }
  ) => {
    const mainCircleOptions = { color };
    const circleCenterOptions = { color, fillStyle: 'yellow' };
    const centerRadius = 3;

    if (femoralHeadCenter && acetabularCavityCenter) {
      drawLine(ctx, element, femoralHeadCenter, acetabularCavityCenter, { color });
    }

    drawnCircleWithCenter(
      ctx,
      element,
      femoralHeadCenter,
      scaleDistanceToCanvas(element, femoralHeadRadius),
      mainCircleOptions,
      centerRadius,
      circleCenterOptions
    );
    drawnCircleWithCenter(
      ctx,
      element,
      acetabularCavityCenter,
      scaleDistanceToCanvas(element, acetabularCavityRadius),
      mainCircleOptions,
      centerRadius,
      circleCenterOptions
    );

    if (femoralHeadCenter && acetabularCavityCenter && distractionIndex) {
      const { x, y } = csc.pixelToCanvas(
        element,
        projectPerpendicularToAxis(
          element,
          getMiddlePoint(femoralHeadCenter, acetabularCavityCenter),
          femoralHeadCenter,
          acetabularCavityCenter,
          15
        ) as csc.PixelCoordinate
      );
      ctx.save();
      ctx.fillStyle = color;
      ctx.textAlign = 'center';
      ctx.font = `${textStyle.getFontSize()}px sans-serif`;
      ctx.fillText(distractionIndex.toFixed(2), x, y);
      ctx.restore();
    }
  };

  drawTool = (
    element: HTMLElement,
    ctx: CanvasRenderingContext2D,
    data: DistractionIndexAnnotation
  ) => {
    const color = toolColors.getColorIfActive(data);
    const { handleRadius, drawHandlesOnHover, hideHandlesIfMoving } = this.configuration;

    const handleOptions = {
      color,
      handleRadius,
      drawHandlesIfActive: drawHandlesOnHover,
      hideHandlesIfMoving,
    };

    drawHandles(ctx, { element }, data.handles, handleOptions);

    if (data.invalidated) {
      this.updateCachedStats(undefined, element, data);
    }
    this.drawDistractionIndex(element, ctx, data.distractionIndexPoints1, { color });
    this.drawDistractionIndex(element, ctx, data.distractionIndexPoints2, { color });
  };

  renderToolData = (evt: RenderEventWithCornerstoneDetail<DistractionIndexAnnotation>): void => {
    const eventData = evt.detail;
    const { element } = eventData;
    // If we have no toolData for this element, return immediately as there is nothing to do
    const toolData = getToolState(evt.currentTarget, this.name);
    const { getIntl, dicomDataPrinterToolName } = this.configuration;

    // We have tool data for this element - iterate over each one and draw it
    const context = getNewContext(eventData.canvasContext.canvas);
    draw(
      context,
      /** @param {CanvasRenderingContext2D} ctx */
      (ctx: CanvasRenderingContext2D) => {
        const dicomDataPrinterTextOffset =
          getToolForElement(element, dicomDataPrinterToolName)?.dicomTextArea?.topLeft?.y ?? 0;

        drawToolStepExplanations(
          ctx,
          toolData?.data,
          getToolForElement(element, this.name).mode === 'active',
          ['tools.distraction_index.steps.1', 'tools.distraction_index.steps.2'].map(
            (id) => () => getIntl()?.formatMessage({ id })
          ),
          [
            ['CTF11', 'CTF12', 'CTF13', 'CTF21', 'CTF22', 'CTF23'],
            ['CCA11', 'CCA12', 'CCA13', 'CCA21', 'CCA22', 'CCA23'],
          ],
          { x: 5, y: dicomDataPrinterTextOffset + 20 },
          { maxWidth: (2 * eventData.canvasContext.canvas.width) / 5 }
        );
        toolData?.data.forEach((data) => {
          if (this.toolBuilder.checkToolCreationCancelled(element, data)) return;
          this.drawTool(element, ctx, data);
        });
      }
    );
  };
}
